import { ErrorMessage } from '@occc-app/lib/ErrorMessage';

const CREATE_CSV_UPLOAD_ERROR = 'app/CSVUpload/CREATE_CSV_UPLOAD_ERROR';
const CREATE_CSV_UPLOAD_OK = 'app/CSVUpload/CREATE_CSV_UPLOAD_OK';
const CREATE_CSV_UPLOAD = 'app/CSVUpload/CREATE_CSV_UPLOAD';
const POLLED_REQUEST_CSV_UPLOAD = 'app/CSVUpload/POLLED_REQUEST_CSV_UPLOAD';
const POLLED_REQUEST_CSV_UPLOADS = 'app/CSVUpload/POLLED_REQUEST_CSV_UPLOADS';
const REQUEST_CSV_UPLOAD = 'app/CSVUpload/REQUEST_CSV_UPLOAD';
const REQUEST_CSV_UPLOAD_ERR = 'app/CSVUpload/REQUEST_CSV_UPLOAD_ERR';
const REQUEST_CSV_UPLOAD_OK = 'app/CSVUpload/REQUEST_CSV_UPLOAD_OK';
const REQUEST_CSV_UPLOADS = 'app/CSVUpload/REQUEST_CSV_UPLOADS';
const REQUEST_CSV_UPLOADS_ERR = 'app/CSVUpload/REQUEST_CSV_UPLOADS_ERR';
const REQUEST_CSV_UPLOADS_OK = 'app/CSVUpload/REQUEST_CSV_UPLOADS_OK';

type Types = {
  CREATE_CSV_UPLOAD_ERROR: typeof CREATE_CSV_UPLOAD_ERROR;
  CREATE_CSV_UPLOAD_OK: typeof CREATE_CSV_UPLOAD_OK;
  CREATE_CSV_UPLOAD: typeof CREATE_CSV_UPLOAD;
  POLLED_REQUEST_CSV_UPLOAD: typeof POLLED_REQUEST_CSV_UPLOAD;
  POLLED_REQUEST_CSV_UPLOADS: typeof POLLED_REQUEST_CSV_UPLOADS;
  REQUEST_CSV_UPLOAD: typeof REQUEST_CSV_UPLOAD;
  REQUEST_CSV_UPLOAD_ERR: typeof REQUEST_CSV_UPLOAD_ERR;
  REQUEST_CSV_UPLOAD_OK: typeof REQUEST_CSV_UPLOAD_OK;
  REQUEST_CSV_UPLOADS: typeof REQUEST_CSV_UPLOADS;
  REQUEST_CSV_UPLOADS_ERR: typeof REQUEST_CSV_UPLOADS_ERR;
  REQUEST_CSV_UPLOADS_OK: typeof REQUEST_CSV_UPLOADS_OK;
};

const types: Types = {
  CREATE_CSV_UPLOAD_ERROR,
  CREATE_CSV_UPLOAD_OK,
  CREATE_CSV_UPLOAD,
  POLLED_REQUEST_CSV_UPLOAD,
  POLLED_REQUEST_CSV_UPLOADS,
  REQUEST_CSV_UPLOAD,
  REQUEST_CSV_UPLOAD_ERR,
  REQUEST_CSV_UPLOAD_OK,
  REQUEST_CSV_UPLOADS,
  REQUEST_CSV_UPLOADS_ERR,
  REQUEST_CSV_UPLOADS_OK,
};

const ADD_SEGMENT_MEMBERSHIPS = 'addSegmentMemberships';
type ADD_SEGMENT_MEMBERSHIPS = typeof ADD_SEGMENT_MEMBERSHIPS;
export type CSVUploadAction = ADD_SEGMENT_MEMBERSHIPS;

type AddSegmentMembershipsConfig = {
  segmentId: string;
  hasHeader: boolean;
  membershipColumnIndex: number;
  refetchSegments?: any;
};
export type CSVUploadConfig = AddSegmentMembershipsConfig;

const CREATED = 'created';
const IN_PROGRESS = 'inProgress';
const COMPLETED = 'completed';
const FAILED = 'failed';
type CREATED = typeof CREATED;
type IN_PROGRESS = typeof IN_PROGRESS;
type COMPLETED = typeof COMPLETED;
type FAILED = typeof FAILED;
type CSVUploadState = CREATED | IN_PROGRESS | COMPLETED | FAILED;

export type CSVUpload = {
  id: string;
  action: CSVUploadAction;
  config: CSVUploadConfig;
  failedCount: number;
  fileName: string;
  processedCount: number;
  state: CSVUploadState;
  totalCount: number;
};

/* Actions */
type CreateCSVUpload = (
  action: CSVUploadAction,
  config: CSVUploadConfig,
  file: File,
  chainId: string,
) => {
  type: typeof CREATE_CSV_UPLOAD;
  action: CSVUploadAction;
  config: CSVUploadConfig;
  file: File;
  chainId: string;
};

type CreateCSVUploadERR = (
  errorMessages: ErrorMessage[],
  error: Error,
) => {
  type: typeof CREATE_CSV_UPLOAD_ERROR;
  errorMessages: ErrorMessage[];
  error: Error;
};

type CreateCSVUploadOK = (csvUpload: CSVUpload) => {
  type: typeof CREATE_CSV_UPLOAD_OK;
  csvUpload: CSVUpload;
};

type PolledCSVUpload = (
  csvUploadId: string,
  refetchSegments: () => void | undefined,
  chainId: string,
) => {
  type: typeof POLLED_REQUEST_CSV_UPLOAD;
  csvUploadId: string;
  refetchSegments: () => void | undefined;
  chainId: string;
};

type PolledCSVUploadsParams = {
  action?: string;
  csvUploadIds?: string[];
  polled?: boolean;
  chainId: string;
};
type PolledCSVUploads = (params: PolledCSVUploadsParams) => {
  type: typeof POLLED_REQUEST_CSV_UPLOADS;
  action?: string;
  csvUploadIds?: string[];
  polled?: boolean;
  chainId: string;
};

type RequestCSVUpload = (
  csvUploadId: string,
  chainId: string,
) => {
  type: typeof REQUEST_CSV_UPLOAD;
  csvUploadId: string;
  chainId: string;
};

type RequestCSVUploadERR = (
  errorMessages: ErrorMessage[],
  error: Error,
) => {
  type: typeof REQUEST_CSV_UPLOAD_ERR;
  errorMessages: ErrorMessage[];
  error: Error;
};

type RequestCSVUploadOK = (csvUpload: CSVUpload) => {
  type: typeof REQUEST_CSV_UPLOAD_OK;
  csvUpload: CSVUpload;
};

type RequestCSVUploadsParams = {
  action?: string;
  csvUploadIds?: string[];
  chainId: string;
};

type RequestCSVUploads = (params: RequestCSVUploadsParams) => {
  type: typeof REQUEST_CSV_UPLOADS;
  action: string;
  csvUploadIds: string[];
  chainId: string;
};

type RequestCSVUploadsERR = (
  errorMessages: ErrorMessage[],
  error: Error,
) => {
  type: typeof REQUEST_CSV_UPLOADS_ERR;
  errorMessages: ErrorMessage[];
  error: Error;
};

type RequestCSVUploadsOK = (csvUploads: CSVUpload[]) => {
  type: typeof REQUEST_CSV_UPLOADS_OK;
  csvUploads: CSVUpload[];
};

export type Action =
  | ReturnType<CreateCSVUploadERR>
  | ReturnType<CreateCSVUploadOK>
  | ReturnType<CreateCSVUpload>
  | ReturnType<PolledCSVUpload>
  | ReturnType<PolledCSVUploads>
  | ReturnType<RequestCSVUpload>
  | ReturnType<RequestCSVUploadERR>
  | ReturnType<RequestCSVUploadOK>
  | ReturnType<RequestCSVUploads>
  | ReturnType<RequestCSVUploadsERR>
  | ReturnType<RequestCSVUploadsOK>;

export type ActionCreators = {
  CreateCSVUploadERR: CreateCSVUploadERR;
  CreateCSVUploadOK: CreateCSVUploadOK;
  CreateCSVUpload: CreateCSVUpload;
  PolledCSVUpload: PolledCSVUpload;
  PolledCSVUploads: PolledCSVUploads;
  RequestCSVUpload: RequestCSVUpload;
  RequestCSVUploadERR: RequestCSVUploadERR;
  RequestCSVUploadOK: RequestCSVUploadOK;
  RequestCSVUploads: RequestCSVUploads;
  RequestCSVUploadsERR: RequestCSVUploadsERR;
  RequestCSVUploadsOK: RequestCSVUploadsOK;
};

export type State = {
  csvUploads: CSVUpload[];
};

export default types;
